<template>
  <div>
    <navbar :links="links" />
    <v-layout
      column
      align-center
    >
      <!-- Search -->
      <v-text-field
        v-model="searchText"
        class="searchbar-input"
        single-line
        hide-details
        :label="$t('search') + '...'"
        append-icon="search"
        clearable
        @click:clear="(() => searchText = '')"
        @keyup.native.esc="searchText = ''"
      />
      <div
        v-if="downloadFiles.length"
        class="grey--text mt-2 mb-2"
      >
        {{ filteredDownloads.length + ' ' + $t('downloadsFound') }}
      </div>
    </v-layout>
    <!-- Loading Indicator -->
    <div class="loading-indicator">
      <v-progress-circular
        v-if="loading"
        :size="50"
        indeterminate
        class="mt-5 msr--text"
      />
    </div>
    <!-- 'No files' -->
    <div
      v-if="!loading && !filteredDownloads.length"
      class="mt-2"
    >
      {{ $t('noDownloadsForSearch') }}
    </div>
    <!-- Download Files -->
    <v-list
      v-if="filteredDownloads.length"
      dense
    >
      <template
        v-for="(downloads, groupKey) in filteredDownloadsGroupedByMonth"
      >
        <v-list-tile
          :key="'tile_' + groupKey"
          class="list-divider-tile elevation-1"
        >
          <v-subheader class="list-divider-subheader">
            {{ getDividerTitleFromGroupKey(groupKey) }}
          </v-subheader>
        </v-list-tile>
        <v-list
          :key="'list_' + groupKey"
          three-line
          dense
          avatar
        >
          <template v-for="(download, downloadIndex) in downloads">
            <v-list-tile
              :key="downloadIndex"
              class="download-tile"
            >
              <v-list-tile-content>
                <v-list-tile-title>{{ download.filename }}</v-list-tile-title>
                <v-list-tile-sub-title style="display: flex;">
                  <span>{{ download.date | dayjs('DD.MM.YYYY') }}</span>
                  <span style="margin-left: auto;">{{
                    formatFileSize(download.size)
                  }}</span>
                </v-list-tile-sub-title>
              </v-list-tile-content>
              <v-avatar class="ml-2">
                <a
                  class="download-link"
                  :href="download.full_download_url"
                  download
                  @click="alertAboutDownload(download.filename)"
                >
                  <v-icon
                    x-large
                    class="msr--text"
                  >file_download</v-icon>
                </a>
              </v-avatar>
            </v-list-tile>
            <v-divider
              v-if="downloadIndex !== downloads.length - 1"
              :key="'divider_' + downloadIndex"
            />
          </template>
        </v-list>
      </template>
    </v-list>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import filter from 'lodash.filter';
import groupBy from 'lodash.groupby';

import matchesFilter from '@/matchesFilter';

import Navbar from '@/components/Navbar';

export default {
  name: 'Downloads',
  components: {
    Navbar,
  },
  data() {
    return {
      links: ['dashboard', 'downloads'],
      searchText: '',
      loading: false,
      downloadFiles: [],
    };
  },
  computed: {
    filteredDownloads() {
      const searchForThis = this.searchText || '';

      return filter(this.downloadFiles, (download) => {
        const valid = matchesFilter(
          [
            download.filename,
            dayjs(download.date).format('DD.MM.YYYY'),
            dayjs(download.expiration_date).format('DD.MM.YYYY'),
            this.formatFileSize(download.size),
          ],
          searchForThis
        );
        return valid;
      });
    },
    /**
     * Group all downloads by a key like: 'month and year'.
     */
    filteredDownloadsGroupedByMonth() {
      return groupBy(this.filteredDownloads, (download) => {
        const date = new Date(download.date);
        const monthIndex = date.getMonth();
        const year = date.getFullYear();
        return monthIndex + ' ' + year;
      });
    },
  },
  mounted() {
    this.loading = true;
    this.$api.listUserDownloadFiles().then((response) => {
      this.downloadFiles = response.data;
      this.loading = false;

      this.$api.updateUserLastSeen('downloads').then(() => {
        this.$store.dispatch('RETRIEVE_NUM_UNSEEN');
      });
    });
  },
  methods: {
    alertAboutDownload(fileName) {
      this.$bus.$emit('alert-info', {
        message: 'Download: ' + fileName,
        timeout: 6000,
        translate: false,
      });
    },
    getDividerTitleFromGroupKey(groupKey) {
      let tokens = groupKey.split(' ');
      const monthIndex = tokens[0];
      const year = tokens[1];

      let date = new Date();
      date.setMonth(monthIndex);
      const monthName = date.toLocaleString(this.$i18n.locale, {
        month: 'long',
      });
      return monthName + ' ' + year;
    },
    formatFileSize(fileSize) {
      let kb = fileSize / 1000;
      let mb = kb / 1000;
      let mbClamped = Number(mb.toFixed(1)) > 0;
      let result =
        (mbClamped ? mb : kb).toFixed(1) + ' ' + (mbClamped ? 'MB' : 'KB');
      return result;
    },
  },
};
</script>

<style>
.download-tile .v-list__tile__title,
.download-tile .v-list__tile__sub-title {
  white-space: initial;
  height: auto;
  word-break: break-word;
}
</style>

<style scoped>
.searchbar-input {
  max-width: 400px;
}

.download-link {
  text-decoration: none;
}
</style>



// WEBPACK FOOTER //
// src/pages/Downloads.vue